import { track } from '@vercel/analytics'

// Custom event tracking for AI Journey Platform
export const analyticsEvents = {
  // User onboarding events
  onboardingStarted: () => {
    track('onboarding_started')
  },

  onboardingCompleted: (userData: {
    businessType?: string
    industry?: string
    aiExperience: string
    primaryGoals?: string[]
    emailConsent?: boolean
  }) => {
    const trackingData: Record<string, any> = {
      ai_experience: userData.aiExperience,
      goals_count: userData.primaryGoals?.length || 0,
      top_goal: userData.primaryGoals?.[0] || 'none'
    }
    
    if (userData.businessType) trackingData.business_type = userData.businessType
    if (userData.industry) trackingData.industry = userData.industry
    if (userData.emailConsent !== undefined) trackingData.email_consent = userData.emailConsent
    
    track('onboarding_completed', trackingData)
  },

  // Journey stage events
  stageStarted: (stageId: number, stageName: string) => {
    track('stage_started', {
      stage_id: stageId,
      stage_name: stageName
    })
  },

  stageCompleted: (stageId: number, stageName: string, timeSpent?: number) => {
    const trackingData: Record<string, any> = {
      stage_id: stageId,
      stage_name: stageName
    }
    
    if (timeSpent !== undefined) trackingData.time_spent_seconds = timeSpent
    
    track('stage_completed', trackingData)
  },

  stageSkipped: (stageId: number, stageName: string, reason?: string) => {
    track('stage_skipped', {
      stage_id: stageId,
      stage_name: stageName,
      skip_reason: reason || 'user_choice'
    })
  },

  // Presentation events
  presentationStarted: () => {
    track('presentation_started')
  },

  presentationSlideViewed: (slideIndex: number, slideTitle: string) => {
    track('presentation_slide_viewed', {
      slide_index: slideIndex,
      slide_title: slideTitle
    })
  },

  presentationCompleted: (totalSlides: number, timeSpent?: number) => {
    const trackingData: Record<string, any> = {
      total_slides: totalSlides
    }
    
    if (timeSpent !== undefined) trackingData.time_spent_seconds = timeSpent
    
    track('presentation_completed', trackingData)
  },

  // AI Tools Reference events
  aiToolsPageVisited: () => {
    track('ai_tools_page_visited')
  },

  aiToolCategoryViewed: (category: 'crawl' | 'walk' | 'run') => {
    track('ai_tool_category_viewed', {
      category: category
    })
  },

  aiToolDetailsViewed: (toolName: string, category: string, difficulty: string) => {
    track('ai_tool_details_viewed', {
      tool_name: toolName,
      category: category,
      difficulty: difficulty
    })
  },

  // Email and engagement events
  emailConsentGiven: (userType?: string) => {
    const trackingData: Record<string, any> = {}
    if (userType) trackingData.user_type = userType
    track('email_consent_given', trackingData)
  },

  emailConsentDeclined: (userType?: string) => {
    const trackingData: Record<string, any> = {}
    if (userType) trackingData.user_type = userType
    track('email_consent_declined', trackingData)
  },

  // Admin dashboard events
  adminDashboardAccessed: () => {
    track('admin_dashboard_accessed')
  },

  adminReportExported: (reportType: string) => {
    track('admin_report_exported', {
      report_type: reportType
    })
  },

  // User engagement events
  chatInteraction: (stageId: number, promptLength: number, responseQuality?: number) => {
    const trackingData: Record<string, any> = {
      stage_id: stageId,
      prompt_length: promptLength
    }
    
    if (responseQuality !== undefined) trackingData.response_quality = responseQuality
    
    track('chat_interaction', trackingData)
  },

  dataUpload: (stageId: number, fileType: string, fileSize?: number) => {
    const trackingData: Record<string, any> = {
      stage_id: stageId,
      file_type: fileType
    }
    
    if (fileSize !== undefined) trackingData.file_size_kb = Math.round(fileSize / 1024)
    
    track('data_upload', trackingData)
  },

  workflowCreated: (nodeCount: number, complexity: string) => {
    track('workflow_created', {
      node_count: nodeCount,
      complexity: complexity
    })
  },

  recommendationGenerated: (stageId: number, recommendationType: string, matchScore?: number) => {
    const trackingData: Record<string, any> = {
      stage_id: stageId,
      recommendation_type: recommendationType
    }
    
    if (matchScore !== undefined) trackingData.match_score = matchScore
    
    track('recommendation_generated', trackingData)
  },

  // User journey completion events
  journeyCompleted: (totalTimeMinutes: number, stagesCompleted: number, emailProvided: boolean) => {
    track('journey_completed', {
      total_time_minutes: totalTimeMinutes,
      stages_completed: stagesCompleted,
      email_provided: emailProvided
    })
  },

  journeyAbandoned: (lastStage: number, timeSpent: number, reason?: string) => {
    track('journey_abandoned', {
      last_stage: lastStage,
      time_spent_minutes: timeSpent,
      abandon_reason: reason || 'unknown'
    })
  },

  // Business intelligence events
  goalSelected: (goal: string, userType?: string) => {
    const trackingData: Record<string, any> = { goal: goal }
    if (userType) trackingData.user_type = userType
    track('goal_selected', trackingData)
  },

  industrySelected: (industry: string, businessSize?: string) => {
    const trackingData: Record<string, any> = { industry: industry }
    if (businessSize) trackingData.business_size = businessSize
    track('industry_selected', trackingData)
  },

  // Session and timing events
  sessionStarted: (deviceInfo?: { isMobile: boolean, browser?: string }) => {
    const trackingData: Record<string, any> = {}
    if (deviceInfo?.isMobile !== undefined) trackingData.is_mobile = deviceInfo.isMobile
    if (deviceInfo?.browser) trackingData.browser = deviceInfo.browser
    track('session_started', trackingData)
  },

  pageView: (pageName: string, referrer?: string) => {
    const trackingData: Record<string, any> = { page_name: pageName }
    if (referrer) trackingData.referrer = referrer
    track('page_view', trackingData)
  }
}

// Helper function for timing events
export class AnalyticsTimer {
  private startTime: number
  private eventName: string
  private metadata: Record<string, any>

  constructor(eventName: string, metadata: Record<string, any> = {}) {
    this.startTime = Date.now()
    this.eventName = eventName
    this.metadata = metadata
  }

  finish(additionalMetadata: Record<string, any> = {}) {
    const duration = Math.round((Date.now() - this.startTime) / 1000)
    track(this.eventName, {
      ...this.metadata,
      ...additionalMetadata,
      duration_seconds: duration
    })
    return duration
  }
}

// Helper for identifying user segments for analytics
export const getUserSegment = (user: {
  businessType?: string
  industry?: string
  currentAIExperience: string
  teamSize?: number
  email?: string
  emailConsent?: boolean
}) => {
  // High Value Prospects
  if (user.businessType?.includes('Large') || user.businessType?.includes('Medium') || (user.teamSize && user.teamSize > 10)) {
    return 'high_value_prospect'
  }
  
  // Tech Savvy Users
  if (user.industry === 'Technology' || user.currentAIExperience === 'advanced') {
    return 'tech_savvy'
  }
  
  // AI Beginners
  if (user.currentAIExperience === 'none') {
    return 'ai_beginner'
  }
  
  // Email Subscribers
  if (user.emailConsent && user.email) {
    return 'email_subscriber'
  }
  
  return 'general_user'
}

// Helper for tracking user journey progress
export const trackUserJourneyProgress = (
  currentStage: number,
  totalStages: number,
  userSegment: string,
  timeSpent: number
) => {
  const progressPercentage = Math.round((currentStage / totalStages) * 100)
  
  track('user_journey_progress', {
    current_stage: currentStage,
    total_stages: totalStages,
    progress_percentage: progressPercentage,
    user_segment: userSegment,
    time_spent_minutes: Math.round(timeSpent / 60)
  })
}